/**
 * Valores monetários do painel em centavos (RealCurrencyInput / keepCents).
 * Percentuais (gateway %, markup %) permanecem em pontos percentuais (ex.: 2 = 2%).
 */

import { isInfinityAssistanceProduct } from "../products.assistance-variant";
import { EProductType, type TProduct } from "../products.type";

/** NET + taxa fixa por venda (sem % gateway) — retorno em centavos. */
export function calculateNetPlusFixedFee(
  netPriceCents: number | undefined | null,
  gatewayFeePerSaleCents: number | undefined | null,
): number {
  const net = roundCents(Number(netPriceCents ?? 0));
  const fixed = roundCents(Number(gatewayFeePerSaleCents ?? 0));
  return net + fixed;
}

/** Entrada de preço para listagem: chave i18n do nome do campo + valor em centavos. */
export type TProductPriceEntry = {
  id: string;
  labelKey: string;
  cents: number;
};

const PRICE_FIELD_LABELS = {
  net_price: "products.table.priceField.net_price",
  minimum_price: "products.table.priceField.minimum_price",
  maximum_price: "products.table.priceField.maximum_price",
  commissioned_price: "products.table.priceField.commissioned_price",
  commission_percent: "products.table.priceField.commission_percent",
  net_plus_fixed_fee: "products.table.priceField.net_plus_fixed_fee",
  cost_net_gateway: "products.table.priceField.cost_net_gateway",
  sale_price: "products.table.priceField.sale_price",
  net_premium: "products.table.priceField.net_premium",
  premium_value: "products.table.priceField.premium_value",
  capital_value: "products.table.priceField.capital_value",
  practiced_price: "products.table.priceField.practiced_price",
  liquid_price: "products.table.priceField.liquid_price",
  gross_price: "products.table.priceField.gross_price",
  sale_price_plan: "products.table.priceField.sale_price_plan",
  prefixed_premium: "products.table.priceField.prefixed_premium",
} as const;

function pushPriceEntry(
  target: TProductPriceEntry[],
  id: string,
  labelKey: string,
  value: unknown,
): void {
  const cents = roundCents(Number(value));
  if (!Number.isFinite(cents) || cents <= 0) {
    return;
  }
  if (target.some((e) => e.id === id)) {
    return;
  }
  target.push({ id, labelKey, cents });
}

/** Ordena entradas do menor para o maior valor (mantém um registro por campo). */
export function sortPriceEntries(entries: TProductPriceEntry[]): TProductPriceEntry[] {
  return [...entries].sort((a, b) => a.cents - b.cents);
}

function pushPositiveCents(target: number[], value: unknown): void {
  const n = roundCents(Number(value));
  if (Number.isFinite(n) && n > 0) {
    target.push(n);
  }
}

/** Valores únicos em centavos, ordenados do menor para o maior. */
export function sortPricePoints(points: number[]): number[] {
  const unique = [...new Set(points.map((p) => roundCents(p)))].filter(
    (p) => Number.isFinite(p) && p > 0,
  );
  return unique.sort((a, b) => a - b);
}

function resolveConfigMaximumCents(
  cfg: Record<string, unknown>,
  premiumType: string | undefined,
): number | null {
  const maxRaw = cfg.maximum_price;
  if (maxRaw !== null && maxRaw !== undefined && maxRaw !== "") {
    const max = roundCents(Number(maxRaw));
    if (Number.isFinite(max) && max > 0) {
      return max;
    }
  }
  if (premiumType === "commissioned") {
    const commissioned = roundCents(Number(cfg.commissioned_price ?? 0));
    if (commissioned > 0) {
      return commissioned;
    }
  }
  const { salePrice } = deriveProductPricing(cfg);
  return salePrice > 0 ? salePrice : null;
}

/** Coleta preços nomeados de um config JSON (garantia, roubo, assistência Soulhub). */
export function collectConfigPriceEntries(
  cfg: Record<string, unknown> | null | undefined,
  premiumType?: string,
): TProductPriceEntry[] {
  if (!cfg || typeof cfg !== "object") {
    return [];
  }

  let type = premiumType ?? (cfg.premium_price_type as string | undefined);
  if (type !== "net" && type !== "commissioned") {
    if (cfg.commissioned_price != null) {
      type = "commissioned";
    } else if (cfg.net_price != null) {
      type = "net";
    }
  }

  const entries: TProductPriceEntry[] = [];
  pushPriceEntry(
    entries,
    "minimum_price",
    PRICE_FIELD_LABELS.minimum_price,
    cfg.minimum_price,
  );

  if (type === "commissioned") {
    pushPriceEntry(
      entries,
      "commissioned_price",
      PRICE_FIELD_LABELS.commissioned_price,
      cfg.commissioned_price,
    );
    pushPriceEntry(
      entries,
      "commission_percent",
      PRICE_FIELD_LABELS.commission_percent,
      cfg.commission_percent,
    );
  } else {
    pushPriceEntry(
      entries,
      "net_price",
      PRICE_FIELD_LABELS.net_price,
      cfg.net_price,
    );

    const cost = calculateNetWithGatewayCosts(
      cfg.net_price as number | undefined,
      cfg.gateway_fee_percent as number | undefined,
      cfg.gateway_fee_per_sale as number | undefined,
    );
    pushPriceEntry(
      entries,
      "cost_net_gateway",
      PRICE_FIELD_LABELS.cost_net_gateway,
      cost,
    );

    const { salePrice } = deriveProductPricing(cfg);
    pushPriceEntry(
      entries,
      "sale_price_calculated",
      "products.table.priceField.sale_price_calculated",
      salePrice,
    );
    pushPriceEntry(
      entries,
      "sale_price",
      PRICE_FIELD_LABELS.sale_price,
      cfg.sale_price,
    );
  }

  const maxRaw = cfg.maximum_price;
  if (maxRaw !== null && maxRaw !== undefined && maxRaw !== "") {
    pushPriceEntry(
      entries,
      "maximum_price",
      PRICE_FIELD_LABELS.maximum_price,
      maxRaw,
    );
  } else {
    const max = resolveConfigMaximumCents(cfg, type);
    if (max != null && !entries.some((e) => e.cents === max)) {
      pushPriceEntry(
        entries,
        "maximum_price_derived",
        PRICE_FIELD_LABELS.maximum_price,
        max,
      );
    }
  }

  return sortPriceEntries(entries);
}

/** Coleta pontos de preço de um config JSON (garantia, roubo, assistência Soulhub). */
export function collectConfigPricePoints(
  cfg: Record<string, unknown> | null | undefined,
  premiumType?: string,
): number[] {
  return collectConfigPriceEntries(cfg, premiumType).map((e) => e.cents);
}

const CAPITAL_RANGE_FIELD_MAP: Array<{
  id: string;
  key: keyof typeof PRICE_FIELD_LABELS;
  field: string;
}> = [
  { id: "minimum_price", key: "minimum_price", field: "minimum_price" },
  { id: "net_price", key: "net_price", field: "net_price" },
  { id: "net_premium", key: "net_premium", field: "net_premium" },
  { id: "premium_value", key: "premium_value", field: "premium_value" },
  { id: "practiced_price", key: "practiced_price", field: "practiced_price" },
  { id: "liquid_price", key: "liquid_price", field: "liquid_price" },
  { id: "gross_price", key: "gross_price", field: "gross_price" },
  { id: "maximum_price", key: "maximum_price", field: "maximum_price" },
  { id: "sale_price", key: "sale_price_plan", field: "sale_price" },
  { id: "capital_value", key: "capital_value", field: "capital_value" },
];

/** Coleta preços nomeados de uma faixa de capital / plano. */
export function collectCapitalRangePriceEntries(
  range: Record<string, unknown>,
): TProductPriceEntry[] {
  const entries: TProductPriceEntry[] = [];
  for (const { id, key, field } of CAPITAL_RANGE_FIELD_MAP) {
    pushPriceEntry(entries, id, PRICE_FIELD_LABELS[key], range[field]);
  }
  return sortPriceEntries(entries);
}

/** Coleta pontos de preço de uma faixa de capital / plano. */
export function collectCapitalRangePricePoints(
  range: Record<string, unknown>,
): number[] {
  return collectCapitalRangePriceEntries(range).map((e) => e.cents);
}

export type TProductPreviewContext = {
  cfgObj?: Record<string, unknown> | null;
  premiumPriceType?: "net" | "commissioned";
  capitalRange?: TProduct["capital_ranges"][number];
  planOnly?: boolean;
};

/** Coleta preços nomeados para exibição na listagem expandida. */
export function collectProductPriceEntries(
  product: TProduct,
  context?: TProductPreviewContext,
): TProductPriceEntry[] {
  if (context?.planOnly) {
    return [];
  }

  const isSoulhubAssistance =
    product.type === EProductType.INSURANCE_ASSISTANCE &&
    !isInfinityAssistanceProduct(product);

  if (isSoulhubAssistance && context?.cfgObj) {
    if (context.capitalRange) {
      const rangeEntries = collectCapitalRangePriceEntries(
        context.capitalRange as unknown as Record<string, unknown>,
      );
      if (rangeEntries.length > 0) {
        return rangeEntries;
      }
    }
    return collectConfigPriceEntries(
      context.cfgObj,
      context.premiumPriceType,
    );
  }

  if (
    product.type === EProductType.WARRANTY_INSURANCE ||
    product.type === EProductType.THEFT_INSURANCE
  ) {
    const cfg =
      product.type === EProductType.WARRANTY_INSURANCE
        ? product.warranty_product_config
        : product.theft_product_config;
    const cfgObj =
      cfg && typeof cfg === "object"
        ? (cfg as Record<string, unknown>)
        : context?.cfgObj ?? null;
    if (cfgObj) {
      return collectConfigPriceEntries(
        cfgObj,
        context?.premiumPriceType ??
          (cfgObj.premium_price_type as string | undefined),
      );
    }
  }

  if (context?.capitalRange) {
    return collectCapitalRangePriceEntries(
      context.capitalRange as unknown as Record<string, unknown>,
    );
  }

  const prefixed = (product as { prefixed_premium_value?: number | null })
    .prefixed_premium_value;
  if (
    (product.type === EProductType.AUTO || product.type === EProductType.MOTO) &&
    typeof prefixed === "number" &&
    prefixed > 0
  ) {
    return sortPriceEntries([
      {
        id: "prefixed_premium",
        labelKey: PRICE_FIELD_LABELS.prefixed_premium,
        cents: roundCents(prefixed),
      },
    ]);
  }

  if (product.premium_value != null && Number(product.premium_value) > 0) {
    return sortPriceEntries([
      {
        id: "premium_value",
        labelKey: PRICE_FIELD_LABELS.premium_value,
        cents: roundCents(Number(product.premium_value)),
      },
    ]);
  }

  const ranges = product.capital_ranges ?? [];
  if (ranges.length > 0) {
    const merged: TProductPriceEntry[] = [];
    for (const range of ranges) {
      for (const entry of collectCapitalRangePriceEntries(
        range as unknown as Record<string, unknown>,
      )) {
        if (!merged.some((e) => e.id === entry.id)) {
          merged.push(entry);
        }
      }
    }
    return sortPriceEntries(merged);
  }

  return [];
}

/** Coleta pontos de preço para exibição na listagem expandida. */
export function collectProductPricePoints(
  product: TProduct,
  context?: TProductPreviewContext,
): number[] {
  return collectProductPriceEntries(product, context).map((e) => e.cents);
}

/** NET + (NET × % gateway) + taxa fixa por venda — retorno em centavos. */
export function calculateNetWithGatewayCosts(
  netPriceCents: number | undefined | null,
  gatewayFeePercent: number | undefined | null,
  gatewayFeePerSaleCents: number | undefined | null,
): number {
  const net = Math.round(Number(netPriceCents ?? 0));
  if (!Number.isFinite(net) || net <= 0) {
    const fixed = Math.round(Number(gatewayFeePerSaleCents ?? 0));
    return Number.isFinite(fixed) ? fixed : 0;
  }
  const pct = Number(gatewayFeePercent ?? 0);
  const fixed = Math.round(Number(gatewayFeePerSaleCents ?? 0));
  const percentPart = Number.isFinite(pct) ? Math.round(net * (pct / 100)) : 0;
  const fixedPart = Number.isFinite(fixed) ? fixed : 0;
  return net + percentPart + fixedPart;
}

export const SOULHUB_GATEWAY_SLUG = "soulhub";

/** Arredonda valor em centavos para inteiro. */
export function roundCents(value: number): number {
  return Math.round(value);
}

/** @deprecated use roundCents */
export const roundMoney = roundCents;

/** Preço de venda a partir do custo (centavos). markup_value em % ou centavos conforme o modo. */
export function calculateSalePriceFromCost(
  costCents: number,
  markupMode: "percentage" | "real_currency" | undefined,
  markupValue: number | undefined | null,
  markupEnabled: boolean,
): number {
  const cost = roundCents(costCents);
  if (!markupEnabled) {
    return cost;
  }
  if (markupMode === "percentage") {
    const pct = Number(markupValue ?? 0);
    return roundCents(cost * (1 + pct / 100));
  }
  if (markupMode === "real_currency") {
    return cost + roundCents(Number(markupValue ?? 0));
  }
  return cost;
}

export function deriveProductPricing(cfg: Record<string, unknown>): {
  cost: number;
  salePrice: number;
} {
  const cost = calculateNetWithGatewayCosts(
    cfg.net_price as number | undefined,
    cfg.gateway_fee_percent as number | undefined,
    cfg.gateway_fee_per_sale as number | undefined,
  );
  const salePrice = calculateSalePriceFromCost(
    cost,
    cfg.markup_mode as "percentage" | "real_currency" | undefined,
    cfg.markup_value as number | undefined | null,
    Boolean(cfg.markup_enabled),
  );
  return { cost, salePrice };
}

function formatBrl(centavos: number): string {
  const reais = centavos / 100;
  return reais.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export { formatBrl as formatProductPriceBrl };

/** Sincroniza custo calculado (NET + gateway) em todos os planos antes do submit. */
export function syncCalculatedCostToPlans(
  data: Record<string, unknown>,
  cfgPrefix: string,
  assistanceSoulhubMode: boolean,
): Record<string, unknown> {
  const cfg = data[cfgPrefix] as Record<string, unknown> | undefined;
  if (!cfg) return data;

  const { cost } = deriveProductPricing(cfg);

  if (assistanceSoulhubMode) {
    const ranges = data.capital_ranges;
    if (Array.isArray(ranges)) {
      data.capital_ranges = ranges.map((row) => {
        const r = row as Record<string, unknown>;
        return { ...r, net_price: cost, premium_value: cost };
      });
    }
  } else {
    const plans = cfg.plans;
    if (Array.isArray(plans)) {
      cfg.plans = plans.map((row) => {
        const p = row as Record<string, unknown>;
        return { ...p, net_price: cost };
      });
    }
  }

  return data;
}

/** Sincroniza sale_price do produto e custo nos planos antes do submit (minimum_price vem do formulário). */
export function syncProductDerivedPricing(
  data: Record<string, unknown>,
  cfgPrefix: string,
  assistanceSoulhubMode: boolean,
  hasPlans: boolean,
): Record<string, unknown> {
  const cfg = data[cfgPrefix] as Record<string, unknown> | undefined;
  if (!cfg) return data;

  if (!hasPlans) {
    if (cfg.premium_price_type === "commissioned") {
      const commissioned = roundCents(Number(cfg.commissioned_price ?? 0));
      if (commissioned > 0) {
        cfg.sale_price = commissioned;
      }
    } else {
      const { salePrice } = deriveProductPricing(cfg);
      cfg.sale_price = salePrice;
    }
  }

  if (cfg.premium_price_type === "commissioned") {
    return data;
  }

  return syncCalculatedCostToPlans(data, cfgPrefix, assistanceSoulhubMode);
}
